import { getSessionData } from '@confluence/session-data';
import type { SessionDataType } from '@confluence/session-data';
import { initializeClientFromValues } from '@confluence/statsig-client/entry-points/initializeClientFromValues';
import { initializeClientWithProvider } from '@confluence/statsig-client/entry-points/initializeClientWithProvider';

// Preload function context is a subset of session data
// It's a subset because we can't provide everything on the server-side.
export type PreloadFnContext = {
	isLoggedIn: SessionDataType['isLoggedIn'];
	/**
	 * @deprecated Use accessStatus
	 */
	isLicensed: SessionDataType['isLicensed'];
	accessStatus: SessionDataType['accessStatus'];
	userId: SessionDataType['userId'];
	cloudId: SessionDataType['cloudId'];
	orgId: SessionDataType['orgId'];
	/**
	 * @deprecated Use `fg()` from `@confluence/feature-gating`;
	 */
	featureFlags: SessionDataType['featureFlags'];
	edition: SessionDataType['edition'];
	activationId: SessionDataType['activationId'];
	environment: SessionDataType['environment'];
	locale: SessionDataType['locale'];
	isAdminHubAIEnabled: SessionDataType['isAdminHubAIEnabled'];
	isRovoEnabled: SessionDataType['isRovoEnabled'];
	isRovoLLMEnabled: SessionDataType['isRovoLLMEnabled'];
};

function sessionDataLikeToPreloadFnContext(data: Partial<SessionDataType>): PreloadFnContext {
	return {
		isLoggedIn: Boolean(data.isLoggedIn),
		isLicensed: Boolean(data.isLicensed),
		accessStatus: data.accessStatus || null,
		userId: data.userId || null,
		cloudId: data.cloudId || '',
		orgId: data.orgId,
		featureFlags: data.featureFlags || {},
		edition: data.edition || null,
		activationId: data.activationId || '',
		environment: data.environment || 'PRODUCTION',
		// Note: the locale from session data is not standard aa-AA format. It is aa_AA
		locale: data.locale?.replace('_', '-') || 'en-US',
		isAdminHubAIEnabled: Boolean(data.isAdminHubAIEnabled),
		isRovoEnabled: Boolean(data.isRovoEnabled),
		isRovoLLMEnabled: Boolean(data.isRovoLLMEnabled),
	};
}

export type getPreloadFnContextType = () => Promise<PreloadFnContext>;

let resolveContext = (_: PreloadFnContext) => {};
const contextPromise: Promise<PreloadFnContext> = new Promise(
	(resolve) => (resolveContext = resolve),
);

export function initializePreloaderFnContextServer(
	contextCreatedFromSSRApp: PreloadFnContext | null = null,
) {
	if (contextCreatedFromSSRApp) {
		resolveContext(sessionDataLikeToPreloadFnContext(contextCreatedFromSSRApp));
	}
}

function getMetadata(name: string) {
	return document.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || '';
}

export async function initializePreloaderFnContext(): Promise<void> {
	if (window.__SSR_RENDERED__) {
		// When SSRed getSessionData is synchronous
		const sessionData = await getSessionData();
		const { userId, cloudId, orgId, environment } = sessionData;

		// __STATSIG_BOOTSTRAP__ is from cc-frontend-ssr or bifrost template
		const bootstrappedOptions = window.__STATSIG_BOOTSTRAP__;
		if (bootstrappedOptions) {
			const { clientSdkKey, experimentValues, configCollectionValues, persistedStatsigOverrides } =
				bootstrappedOptions;
			// Although this function returns a promise, it is actually synchronous when experimentValues are provided
			await initializeClientFromValues({
				identifiers: {
					userId,
					tenantId: cloudId || '',
					orgId,
				},
				environment,
				clientSdkKey,
				experimentValues,
				configCollectionValues,
				persistedStatsigOverrides,
			});
		} else {
			await initializeClientWithProvider({ cloudId, userId, environment });
		}
		resolveContext(sessionDataLikeToPreloadFnContext(sessionData));
	} else {
		const cloudId = getMetadata('ajs-cloud-id');
		const userId = getMetadata('ajs-atlassian-account-id');
		const environment = getMetadata('ajs-environment');

		let sessionData: SessionDataType;
		if (userId && cloudId && environment) {
			// Initialize client in parallel if we have all the metadata
			const promiseAllResult = await Promise.all([
				getSessionData(),
				// No orgId in the metadata
				initializeClientWithProvider({ cloudId, userId, environment }),
			]);
			sessionData = promiseAllResult[0];
		} else {
			// If we don't have all the info. Eg in standalone mode there is no environment metadata
			// Downgrade to waterfall
			sessionData = await getSessionData();
			const { userId, cloudId, environment } = sessionData;
			await initializeClientWithProvider({ cloudId, userId, environment });
		}
		resolveContext(sessionDataLikeToPreloadFnContext(sessionData));
	}
}

export async function getPreloaderFnContext(): Promise<PreloadFnContext> {
	return contextPromise;
}
